/**
 * SOLID Review
 * S — Strong. One concern: turn what a context service asked of the aggregates into the decision the port answers —
 *     the changes that make the client state handed in the state the aggregates leave, and the answer, or the
 *     refusal.
 * O — Good. A new table or session field needs nothing here: the fields are the empty table's and the session's.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Good. Four ways in (a table, a session, a small context's part, nothing at all), two steps a leave adds,
 *     three answers.
 * D — Strong. The two aggregates — the empty table, the derived values, the stored-game rule — the server's dice
 *     ranking, the verdict envelope and the schema types. No store, no port.
 */
import { allowed, type IEntityDecision, type TDecisionResult } from './shared';
import { ClientTable } from '../table/ClientTable';
import { DiceRanking } from '../dice/DiceRanking';
import { sameJson } from '../../shared/sameJson';
import type { ClientSession, IConfigSessionKeeps } from '../session/ClientSession';
import type { IStateClientTable, IPropsClientTableInput } from '../ports/schemas/ClientTable';
import type { IStateClientSession, IPropsClientSessionChanges, IPropsClientPersist } from '../ports/schemas/ClientSession';
import type { IPropsClientChanges, IPropsClientInput } from '../ports/schemas/ClientChanges';
import type { IEntityTableAck } from '../ports/schemas/TableEvents';

/** What a client decision answers on the port: its changes and its dto, or a refusal. */
export type TClientDecision<D> = TDecisionResult<IEntityDecision<IPropsClientChanges, D>>;

/**
 * A SMALL CONTEXT'S AGGREGATE, as a decision reads it (frontend-domain-oo P6): the session fields it holds — the
 * consent, the ad slot, the room's media, the credits, the error ledger — as its moves leave them.
 */
export interface IStateClientSessionPart {
  sessionFields(): IPropsClientSessionChanges;
}

/** How a table decision's changes are read. */
export interface IConfigClientDecision {
  /** The table moved: the store stamps `lastGameActivityAt`. */
  readonly markActivity?: boolean;
  /** Back to the store's initial table first; the table is then read against the empty one. */
  readonly reset?: boolean;
}

/** Identity and the activity stamp are read, never changed, by a decision (the store stamps the latter). */
type TTableField = Exclude<keyof IStateClientTable, 'localPlayerId' | 'lastGameActivityAt'>;
/** Every table field a decision may change: all of them but identity and the stamp. */
const TABLE_FIELDS = (Object.keys(ClientTable.empty().toSnapshot()) as Array<keyof IStateClientTable>)
  .filter((k): k is TTableField => k !== 'localPlayerId' && k !== 'lastGameActivityAt');

type TSessionField = keyof IStateClientSession;
/** Every session field, in the order its changes report them. */
const SESSION_FIELDS: ReadonlyArray<TSessionField> = Object.freeze([
  'account', 'player', 'screen', 'pendingRoomCode', 'hostOnEntry', 'pendingJoinError', 'pendingEmail', 'consent', 'ads',
  'media', 'diagnostics', 'diceRanking', 'restoring', 'entering', 'hosting', 'hostError',
] as const);

const NOT_APPLIED: IEntityTableAck = { applied: false };
const APPLIED: IEntityTableAck = { applied: true };

/**
 * A CLIENT DECISION, BUILT FROM THE AGGREGATES IT ASKED (frontend-domain-oo P5, 2026-10-26; handbook §28.272). It
 * replaces the decide functions' two helpers: `settle` (`decisions/table/tableDraft.ts`, FE1), which diffed a table
 * draft into changes, and `settleSession` (`decisions/session/sessionDraft.ts`, FE2), which added the session's half —
 * their semantics kept, word for word below. A context service builds the aggregate from the snapshot, asks it, and
 * answers through this: `ClientDecision.ofTable(input, table).acked(table.rolled(command))`.
 *
 * THE CHANGES ARE READ WHEN THE DECISION ANSWERS, not when it is begun, so the aggregate may be asked in the same
 * expression. A handler's sequence of store setters was replayed on a draft, and the changes report what differs: a
 * later write wins over an earlier one exactly as it did in the store, and a write of the value already held changes
 * nothing.
 *
 * THE CHANGES, for a table: every field that differs (against the empty table after a reset), the derived values of
 * the table left — by the ranking it is played by — when anything does, the two instructions (`reset`,
 * `markActivity`), and what the tab keeps (`persist`: the stored game, which follows the seat — the table's
 * `storedGameSince`). For a session, the table's as above, by the ranking the session leaves; the derived values also
 * when only the local player or the ranking changed (the table's diff sees neither, and they hang on both); the
 * session fields that differ, each whole; and what the session keeps or forgets (the guest identity, the stored game).
 * Nothing differing yields `{}` — or only `markActivity` — and the shell's save is a no-op.
 */
export class ClientDecision {
  private constructor(private readonly changesNow: () => IPropsClientChanges) {}

  /** A table decision: the table the service asked, against the snapshot it was built from. */
  static ofTable(input: IPropsClientTableInput, table: ClientTable, how: IConfigClientDecision = {}): ClientDecision {
    return new ClientDecision(() => ClientDecision.tableChanges(input.table, table.toSnapshot(), DiceRanking.of(input.diceRanking), how));
  }

  /** A session decision: the session the service asked — with the table it sits at — and what the tab keeps. */
  static ofSession(input: IPropsClientInput, session: ClientSession): ClientDecision {
    return new ClientDecision(() => ClientDecision.sessionChanges(input, session.toSnapshot(), session.kept()));
  }

  /**
   * A small context's decision (frontend-domain-oo P6, §28.273): the session fields its aggregate holds, as it leaves
   * them — the rest of the session and the table as they were, nothing kept or forgotten by the tab. It replaces the
   * draft those contexts edited (`draftOf` / `settleSession`), with the same reading.
   */
  static keeping(input: IPropsClientInput, part: IStateClientSessionPart): ClientDecision {
    return new ClientDecision(() => {
      const next: IPropsClientInput = { table: input.table, session: { ...input.session, ...part.sessionFields() } };
      const keepsNothing: IConfigSessionKeeps = {};
      return ClientDecision.sessionChanges(input, next, keepsNothing);
    });
  }

  /** A decision that changes nothing — a plan of the legal side's, or an answer a guard dropped. */
  static unchanged(): ClientDecision {
    return new ClientDecision(() => {
      const nothing: IPropsClientChanges = {};
      return nothing;
    });
  }

  /** The same decision, saying the session's `changes` too — a table decision that sends the player elsewhere. */
  alongside(session: IPropsClientSessionChanges): ClientDecision {
    return new ClientDecision(() => {
      const changes = this.changesNow();
      changes.session = session;
      return changes;
    });
  }

  /** The same decision, and what the tab is to keep or forget beside whatever the seat rule says. */
  persisting(persist: IPropsClientPersist): ClientDecision {
    return new ClientDecision(() => {
      const changes = this.changesNow();
      changes.persist = { ...changes.persist, ...persist };
      return changes;
    });
  }

  /** The answer, with the changes — the one place a client decision's envelope is built. */
  answering<D>(dto: D): TClientDecision<D> {
    const decision: IEntityDecision<IPropsClientChanges, D> = { changes: this.changesNow(), dto };
    return allowed(decision);
  }

  /** An answer or event the rules applied, with the changes — or one their guards dropped, which changes nothing. */
  acked(applied: boolean): TClientDecision<IEntityTableAck> {
    return applied ? this.answering(APPLIED) : ClientDecision.unchanged().answering(NOT_APPLIED);
  }

  /** A rule's verdict: the refusal as it is, or its answer with the changes (a plan may move what it asked first). */
  judging<D>(verdict: TDecisionResult<D>): TClientDecision<D> {
    return verdict.success ? this.answering(verdict.value) : verdict;
  }

  private static tableChanges(base: IStateClientTable, next: IStateClientTable, ranking: DiceRanking, how: IConfigClientDecision): IPropsClientChanges {
    const from = how.reset === true ? ClientTable.empty().toSnapshot() : base;
    const changes: IPropsClientChanges = {};
    for (const key of TABLE_FIELDS) {
      if (!sameJson(from[key], next[key])) ClientDecision.assignTable(changes, key, next[key]);
    }
    if (how.reset === true) changes.reset = true;
    const after = ClientTable.fromSnapshot(next);
    if (how.reset === true || Object.keys(changes).length > 0) changes.derived = after.derived(ranking);
    if (how.markActivity === true) changes.markActivity = true;
    const resume = after.storedGameSince(ClientTable.fromSnapshot(base));
    if (resume !== undefined) changes.persist = { resume };
    return changes;
  }

  private static sessionChanges(base: IPropsClientInput, next: IPropsClientInput, keeps: IConfigSessionKeeps): IPropsClientChanges {
    const ranking = DiceRanking.of(next.session.diceRanking);
    const how: IConfigClientDecision = { reset: keeps.reset };
    const changes = ClientDecision.tableChanges(base.table, next.table, ranking, how);
    const unseen = base.table.localPlayerId !== next.table.localPlayerId || !sameJson(base.session.diceRanking, next.session.diceRanking);
    if (changes.derived === undefined && unseen) changes.derived = ClientTable.fromSnapshot(next.table).derived(ranking);
    const session: IPropsClientSessionChanges = {};
    for (const key of SESSION_FIELDS) {
      if (!sameJson(base.session[key], next.session[key])) ClientDecision.assignSession(session, key, next.session[key]);
    }
    if (Object.keys(session).length > 0) changes.session = session;
    if (keeps.guest !== undefined) changes.persist = { ...changes.persist, guest: keeps.guest };
    if (keeps.resume !== undefined) changes.persist = { ...changes.persist, resume: keeps.resume };
    return changes;
  }

  private static assignTable<K extends TTableField>(changes: IPropsClientChanges, key: K, value: IStateClientTable[K]): void {
    changes[key] = value;
  }

  private static assignSession<K extends TSessionField>(changes: IPropsClientSessionChanges, key: K, value: IStateClientSession[K]): void {
    changes[key] = value;
  }
}
